import { Button, Card, CardBody } from '@fold-and-flow/ui'
import { useNavigate, useParams } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import { trpc } from '#lib/trpc'

const PHASE_LABELS: Record<string, string> = {
  warmup: 'Warm-up',
  main: 'Main practice',
  peak: 'Peak',
  integration: 'Integration',
  savasana: 'Savasana',
}

const PHASE_COLORS: Record<string, string> = {
  warmup: 'bg-terracotta-400/10 text-terracotta-600',
  main: 'bg-sage-400/10 text-sage-700',
  peak: 'bg-warm-gray-200 text-warm-gray-700',
  integration: 'bg-sage-400/10 text-sage-700',
  savasana: 'bg-cream-200 text-warm-gray-500',
}

export function FlowPreviewPage() {
  const { flowId } = useParams({ from: '/_app/flow/$flowId' })
  const navigate = useNavigate()

  const { data: flow, isLoading } = trpc.flow.byId.useQuery({ id: flowId })
  const startSession = trpc.session.start.useMutation({
    onSuccess: async ({ id }) => {
      await navigate({ to: '/session/$sessionId', params: { sessionId: id } })
    },
  })

  if (isLoading) {
    return (
      <div className="max-w-md mx-auto flex flex-col items-center justify-center py-24 gap-4">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Number.POSITIVE_INFINITY, ease: 'linear' }}
          className="w-8 h-8 rounded-full border-2 border-terracotta-400 border-t-transparent"
        />
        <p className="text-warm-gray-400 text-sm">Preparing your flow…</p>
      </div>
    )
  }

  if (!flow) {
    return (
      <div className="max-w-md mx-auto py-24 text-center">
        <p className="text-warm-gray-400">Flow not found.</p>
      </div>
    )
  }

  const totalMin = flow.totalDurationMin

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="max-w-md mx-auto flex flex-col gap-6"
    >
      <div>
        <p className="text-sm text-terracotta-500 font-medium mb-1">Your practice is ready</p>
        <h1 className="font-serif text-3xl text-warm-gray-900">Today's Flow</h1>
        <p className="text-warm-gray-400 mt-1">{totalMin} minutes</p>
      </div>

      {flow.reasoning && (
        <Card elevated className="bg-gradient-to-br from-cream-50 to-white">
          <CardBody>
            <p className="text-sm text-warm-gray-600 leading-relaxed italic">"{flow.reasoning}"</p>
          </CardBody>
        </Card>
      )}

      <div className="flex flex-col gap-3">
        {flow.items.map((item, i: number) => {
          const label = PHASE_LABELS[item.phase] ?? item.phase
          const color = PHASE_COLORS[item.phase] ?? 'bg-cream-200 text-warm-gray-600'
          return (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, x: -12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.07, duration: 0.3 }}
              className="flex items-center gap-4 bg-white rounded-2xl border border-cream-200 px-5 py-4"
            >
              <div className="flex-shrink-0 w-7 h-7 rounded-full bg-cream-100 flex items-center justify-center text-xs font-medium text-warm-gray-500">
                {i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-warm-gray-800 truncate">
                  {item.clipName ?? item.clipId}
                </p>
                <span className={`inline-block mt-0.5 px-2 py-0.5 rounded-full text-xs ${color}`}>
                  {label}
                </span>
              </div>
              <span className="text-xs text-warm-gray-400 flex-shrink-0 tabular-nums">
                {item.durationOverrideMin}m
              </span>
            </motion.div>
          )
        })}
      </div>

      <Button
        size="lg"
        className="w-full mt-2"
        loading={startSession.isPending}
        onClick={() => startSession.mutate({ flowId })}
      >
        Begin practice →
      </Button>
    </motion.div>
  )
}
